import { useMemo, useState } from 'react';
import { useObservableState } from 'observable-hooks';
import { catchError, map, of, startWith } from 'rxjs';
import Button from '../../form/Button';
import { ModalDialog } from '../../modal/layout/ModalDialog';
import { ModalHeader } from '../../modal/layout/ModalHeader';
import type { RenderRuntimeDataServices } from './elements/types';
import { createBindingId } from './runtime/dataServices/bindingCatalog';

// Internal diagnostics panel for runtime data-service troubleshooting.
interface RuntimeDataServiceDebugPanelProps {
    open: boolean;
    onClose(this: void): void;
    dataServices: RenderRuntimeDataServices | null;
}

type PreviewState =
    | { status: 'loading' }
    | { status: 'value'; value: unknown }
    | { status: 'error'; message: string };

function formatJson(value: unknown): string {
    try {
        return JSON.stringify(value, null, 2);
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return `<<unserializable: ${message}>>`;
    }
}

function BindingPreview({
    dataServices,
    bindingInstanceKey,
    inputKey,
}: {
    dataServices: RenderRuntimeDataServices;
    bindingInstanceKey: string;
    inputKey: string;
}): JSX.Element {
    const preview$ = useMemo(
        () =>
            dataServices.debug
                ? dataServices.debug.observeActiveBindingInput(bindingInstanceKey, inputKey).pipe(
                      map(
                          (value): PreviewState => ({
                              status: 'value',
                              value,
                          })
                      ),
                      startWith({ status: 'loading' } as PreviewState),
                      catchError((error) =>
                          of({
                              status: 'error',
                              message: error instanceof Error ? error.message : String(error),
                          } as PreviewState)
                      )
                  )
                : of({
                      status: 'error',
                      message: gettext('DEBUG_RUNTIME_API_UNAVAILABLE'),
                  } as PreviewState),
        [bindingInstanceKey, dataServices.debug, inputKey]
    );
    const state = useObservableState(preview$, { status: 'loading' } as PreviewState);

    if (state.status === 'loading') {
        return <p className="text-xs text-gray-500">{gettext('LOADING')}</p>;
    }

    if (state.status === 'error') {
        return <p className="text-xs text-red-600">{state.message}</p>;
    }

    return (
        <pre className="max-h-56 overflow-auto rounded border bg-gray-50 p-2 text-[11px]">
            {formatJson(state.value)}
        </pre>
    );
}

function ActiveBindingRow({
    bindingInstanceKey,
    debugApi,
    dataServices,
    openPreviewKey,
    onTogglePreview,
}: {
    bindingInstanceKey: string;
    debugApi: NonNullable<RenderRuntimeDataServices['debug']>;
    dataServices: RenderRuntimeDataServices;
    openPreviewKey: string | null;
    onTogglePreview: (previewKey: string) => void;
}): JSX.Element | null {
    const bindingDetails$ = useMemo(
        () => debugApi.observeActiveBindingDebug(bindingInstanceKey),
        [bindingInstanceKey, debugApi]
    );
    const bindingDetails = useObservableState(bindingDetails$);

    if (typeof bindingDetails === 'undefined') {
        return null;
    }

    return (
        <div className="rounded border border-gray-200 p-3">
            <div className="text-xs text-gray-600">
                <strong>{bindingDetails.binding.serviceId}</strong> /{' '}
                <span>{bindingDetails.binding.linkKey}</span>
                {typeof bindingDetails.binding.arg !== 'undefined' ? (
                    <span>
                        {' '}
                        / {gettext('ARGUMENT')}: {String(bindingDetails.binding.arg)}
                    </span>
                ) : null}
            </div>
            {bindingDetails.inputBuckets.length === 0 ? (
                <p className="mt-2 text-xs text-gray-500">{gettext('NO_INPUT_BUCKETS')}</p>
            ) : (
                <div className="mt-2 space-y-3">
                    {bindingDetails.inputBuckets.map((bucket) => {
                        const previewKey = `${bindingDetails.bindingInstanceKey}:${bucket.inputKey}`;
                        const isOpen = previewKey === openPreviewKey;
                        const observerPaths =
                            bucket.observers.length > 0
                                ? bucket.observers.map((observer) => observer.elementPath)
                                : [bucket.inputKey];

                        return (
                            <div key={bucket.inputKey} className="rounded border p-2">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1 space-y-1">
                                        <div className="max-h-24 space-y-1 overflow-auto pr-1">
                                            {observerPaths.map((observerPath, observerIndex) => (
                                                <code
                                                    key={`${observerPath}-${observerIndex}`}
                                                    className="block w-full truncate rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700"
                                                    title={observerPath}
                                                >
                                                    {observerPath}
                                                </code>
                                            ))}
                                        </div>
                                        <div className="text-xs text-gray-700">
                                            <strong>{gettext('OBSERVERS')}:</strong>{' '}
                                            {bucket.observerCount}
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="neutral"
                                        className="shrink-0"
                                        onClick={() => onTogglePreview(previewKey)}
                                    >
                                        {isOpen ? gettext('HIDE') : gettext('SHOW')}
                                    </Button>
                                </div>
                                {isOpen ? (
                                    <div className="mt-2 space-y-2">
                                        <div>
                                            <p className="mb-1 text-[11px] font-medium text-gray-700">
                                                {gettext('INPUT_KEY')}
                                            </p>
                                            <pre className="max-h-28 overflow-auto rounded border bg-gray-50 p-2 text-[11px]">
                                                {formatJson(bucket.input)}
                                            </pre>
                                        </div>
                                        <div>
                                            <p className="mb-1 text-[11px] font-medium text-gray-700">
                                                {gettext('VALUE')}
                                            </p>
                                            <BindingPreview
                                                dataServices={dataServices}
                                                bindingInstanceKey={
                                                    bindingDetails.bindingInstanceKey
                                                }
                                                inputKey={bucket.inputKey}
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export function RuntimeDataServiceDebugPanel({
    open,
    onClose,
    dataServices,
}: RuntimeDataServiceDebugPanelProps): JSX.Element {
    const [openPreviewKey, setOpenPreviewKey] = useState<string | null>(null);
    const debugApi = dataServices?.debug;
    const activeBindingInstanceKeys$ = useMemo(
        () =>
            debugApi ? debugApi.observeActiveBindingInstanceKeys() : of([] as readonly string[]),
        [debugApi]
    );
    const activeBindingInstanceKeys = useObservableState(activeBindingInstanceKeys$, []);
    const catalog = useMemo(() => debugApi?.listCatalog() ?? [], [debugApi]);

    function togglePreview(previewKey: string): void {
        setOpenPreviewKey((previous) => (previous === previewKey ? null : previewKey));
    }

    return (
        <ModalDialog open={open} onClose={onClose} closeOnClickOutside={true} closeOnEsc={true}>
            <div className="relative flex max-h-[80vh] flex-col py-6">
                <ModalHeader title={gettext('RUNTIME_DATA_SERVICE_DEBUG')} onClose={onClose} />
                <div className="space-y-6 overflow-auto px-6">
                    {!debugApi ? (
                        <p className="text-sm text-gray-700">
                            {gettext('DEBUG_RUNTIME_API_UNAVAILABLE')}
                        </p>
                    ) : (
                        <>
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold text-gray-900">
                                    {gettext('CATALOG')}
                                </h3>
                                {catalog.length === 0 ? (
                                    <p className="text-xs text-gray-500">
                                        {gettext('NO_DATA_SERVICES_REGISTERED')}
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {catalog.map((service) => (
                                            <div
                                                key={service.serviceId}
                                                className="rounded border border-gray-200 p-3"
                                            >
                                                <p className="text-sm font-medium text-gray-900">
                                                    {service.label} ({service.serviceId})
                                                </p>
                                                <p className="mt-1 text-xs text-gray-600">
                                                    {service.description}
                                                </p>
                                                <ul className="mt-2 space-y-1 text-xs text-gray-700">
                                                    {service.links.map((link) => {
                                                        const bindingId = createBindingId(
                                                            service.serviceId,
                                                            link.key
                                                        );

                                                        return (
                                                            <li key={bindingId}>
                                                                <strong>{bindingId}</strong> -{' '}
                                                                {link.label}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold text-gray-900">
                                    {gettext('ACTIVE_BINDINGS')}
                                </h3>
                                {activeBindingInstanceKeys.length === 0 ? (
                                    <p className="text-xs text-gray-500">
                                        {gettext('NO_ACTIVE_BINDINGS')}
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {dataServices
                                            ? activeBindingInstanceKeys.map(
                                                  (bindingInstanceKey) => (
                                                      <ActiveBindingRow
                                                          key={bindingInstanceKey}
                                                          bindingInstanceKey={bindingInstanceKey}
                                                          debugApi={debugApi}
                                                          dataServices={dataServices}
                                                          openPreviewKey={openPreviewKey}
                                                          onTogglePreview={togglePreview}
                                                      />
                                                  )
                                              )
                                            : null}
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                </div>
                <div className="mt-6 px-6">
                    <div className="flex justify-end">
                        <Button variant="neutral" onClick={onClose} type="button">
                            {gettext('CLOSE')}
                        </Button>
                    </div>
                </div>
            </div>
        </ModalDialog>
    );
}
